import { Eフェーズ種別, E有無フラグ, E計画表示モード } from '@/openapi/enum';

import AppFormatUtility from '@/classes/common/AppFormatUtility';
import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import StorageDao from '@/classes/common/StorageDao';

export type 決算期 = {
  header: 決算期TableHeader[];
  years: 決算年[];
  fixMonth?: number;
};
export type 決算期TableHeader = {
  label: string;
  year: number;
  決算年: number;
  month?: number;
  isSummary?: boolean;
  disabled?: boolean;
};
export type 決算年 = {
  year: number;
  eフェーズ種別: Eフェーズ種別;
  is計画01: boolean;
};

export default class 決算期Utility {
  /** 過去の決算期数 */
  public static readonly 過去決算期数 = 5;

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 指定決算期から指定決算期数をさかのぼった決算期一覧を取得します。
   *
   * @param 決算期
   * @param 決算期数
   * @param within決算期
   * @returns
   */
  public static get決算期Yms(決算期: number, 決算期数: number, within決算期 = false): number[] {
    const 決算月 = this.getMonth(決算期);
    const 決算年s = this.get決算年s(決算期, 決算期数, within決算期);
    return 決算年s.map((年) => 年 * 100 + 決算月);
  }

  /**
   * yyyyMMから年を抽出します。
   *
   * @param ym
   * @returns
   */
  public static getYear(ym: number): number {
    return Math.floor(ym / 100);
  }

  /**
   * yyyyMMから月を抽出します。
   *
   * @param ym
   * @returns
   */
  public static getMonth(ym: number): number {
    return ym % this.getYear(ym);
  }

  /**
   * 指定決算期から指定決算期数をさかのぼった決算年一覧を取得します。
   *
   * @param 決算期
   * @param 決算期数
   * @param within決算期
   * @returns
   */
  public static get決算年s(決算期: number, 決算期数: number, within決算期 = false): number[] {
    let 決算年 = this.getYear(決算期);
    if (within決算期) 決算年 += 1;
    return [...Array(決算期数)].map((v, i) => 決算年 - 決算期数 + i);
  }

  /**
   * 指定決算年配列と指定決算月から「{決算年}年{決算月}月期」の表示文字列一覧を取得します
   * 指定により「計画第{i}期」を付けます
   * 指定により'YYYY年'と'M月期'の間に改行をいれます
   *
   * @param 決算年s
   * @param 決算月
   * @param is計画Prefix
   * @param isWithNL
   * @returns
   */
  // TODO: isWithNL が必要かどうか後で見直し
  public static get決算期Labels(決算年s: number[], 決算月: number, is計画Prefix = false, isWithNL = false): string[] {
    return 決算年s.map((year, i) => {
      const prefix = is計画Prefix ? `計画第${i}期\n` : '';
      return `${prefix}${AppFormatUtility.format年月期(`${year}${決算月}`, isWithNL)}`;
    });
  }

  /**
   * 分析用の決算期を取得します（分析決算期）
   *
   * @param 分析決算期Count
   * @returns
   */
  public static get分析決算期(分析決算期Count: number): 決算期 {
    return this.get決算期(分析決算期Count);
  }

  /**
   * 計画用の決算期を取得します（分析決算期、0期月次、1期月次、計画決算期）
   *
   * @returns
   */
  public static getAll決算期(): 決算期 {
    return this.get決算期(this.過去決算期数, [0, 1], true, true);
  }

  /**
   * 計画用の決算期を取得します（分析決算期、計画決算期）
   *
   * @param 分析決算期Count
   * @param isWith決算期Month
   * @returns
   */
  public static get計画決算期(分析決算期Count = 0, isWith決算期Month = false): 決算期 {
    const 決算期 = this.get決算期(分析決算期Count, [], true, true, isWith決算期Month);
    return 決算期;
  }

  /**
   * 計画用の決算期を取得します（0期月次、計画決算期）
   *
   * @returns
   */
  public static get計画決算期With0期月次(isWith決算期Month = false): 決算期 {
    return this.get決算期(0, [0], true, true, isWith決算期Month);
  }

  /**
   * 指定年が計画0期 or 1期かどうか判定します
   *
   * @param year
   * @returns
   */
  public static is計画01(year: number): boolean {
    const diffYear = this.diffYearFrom0期(year);
    return diffYear === 0 || diffYear === 1;
  }

  /**
   * 0期からの差分を返却します
   *
   * @param year
   * @returns
   */
  public static diffYearFrom0期(year: number, isYearConfig = false): number {
    return year - this.get計画0期の決算年(isYearConfig);
  }

  /**
   * 計画0期の年月一覧を取得
   *
   * @return
   */
  public static get計画0期年月s(): { year: number; month: number }[] {
    const 決算期 = StorageDao.get計画書().c計画0期の決算期;
    const 期首年月 = 決算期 - (決算期 % 100 === 12 ? 11 : 99);

    return Array.from({ length: MONTHS_OF_YEAR }).map((_, index) => {
      const mIndex = (期首年月 % 100) - 1 + index;
      return { year: Math.floor(期首年月 / 100) + Math.floor(mIndex / 12), month: (mIndex % 12) + 1 };
    });
  }

  /**
   * 計画1期の年月一覧を取得
   *
   * @return
   */
  public static get計画1期年月s(): { year: number; month: number }[] {
    return this.get計画期年月s(1);
  }

  /**
   * 指定期の年月一覧を取得
   *
   * @param 期数
   * @return
   */
  public static get計画期年月s(期数: number): { year: number; month: number }[] {
    return this.get計画0期年月s().map((row) => ({ year: row.year + 期数, month: row.month }));
  }

  /**
   * 「c計画0期の決算期」の年のみ取得します
   *
   * @returns
   */
  public static get計画0期の決算年(isYearConfig = false): number {
    const 計画書 = StorageDao.get計画書();
    const 計画0期Year = this.getYear(計画書.c計画0期の決算期);
    return isYearConfig && 計画書.c第1期月次作成フラグ === E有無フラグ.あり ? 計画0期Year + 1 : 計画0期Year;
  }

  /**
   * 「E計画表示モード」のみ取得します
   *
   * @returns
   */
  public static checkE計画表示モード(): boolean {
    return StorageDao.get計画書().c計画表示モード === E計画表示モード.年次計画;
  }

  /**
   * 差異/比率ヘッダーを返却します
   *
   * @param label1
   * @param label2
   * @returns
   */
  public static create差異比率Header(label1: string, label2: string): 決算期TableHeader[] {
    const year = 0;
    return [`差異\n(${label1}-${label2})`, `比率\n(${label1}/${label2})`].map((label) => ({
      label,
      year,
      決算年: year,
      isSummary: true,
    }));
  }

  /**
   * モニタリング会計期間を取得します
   *
   * @returns
   */
  public static getモニタリング会計期間(): 決算期 {
    return this.get決算期(0, [], true, false, true);
  }

  /**
   * 業績予測用の決算期を取得します
   * ※前期～指定の期まで取得
   *
   * @param 期数
   * @param isWithPrefix
   * @returns
   */
  public static getモニタリング決算期(期数: number, isWithPrefix?: boolean): 決算期 {
    const 決算期: 決算期 = {
      header: [],
      years: [],
    };

    // 分析
    this.add年次決算期(決算期, Eフェーズ種別.分析フェーズ, -1);

    // モニタリング
    for (let i = 0; i <= 期数; i++) {
      this.add月次決算期s(決算期, isWithPrefix, i);
      this.add年次決算期(決算期, Eフェーズ種別.モニタリングフェーズ, i, true);
    }

    return 決算期;
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 決算期を取得します
   * ※分析決算期Count：必要な分析決算期数を設定、不要なら0とすること
   * ※月次計画期s：0期、1期のうち必要なものを設定、月次が不要なら空配列とすること（例　[0, 1]）
   *
   * @param 分析決算期Count
   * @param 月次計画期s
   * @param include計画決算期
   * @param isWithPrefix
   * @param isWith決算期Month
   * @returns
   */
  private static get決算期(
    分析決算期Count: number,
    月次計画期s: number[] = [],
    include計画決算期 = false,
    isWithPrefix = false,
    isWith決算期Month = false
  ): 決算期 {
    const c計画作成期間 = StorageDao.get事業所().c計画作成期間;
    const 計画書 = StorageDao.get計画書();
    const 決算期: 決算期 = {
      header: [],
      years: [],
    };

    // 分析決算期
    for (let i = 0; i < 分析決算期Count; i++) {
      this.add年次決算期(決算期, Eフェーズ種別.分析フェーズ, i - 分析決算期Count, isWithPrefix);
    }
    // 0期の月次
    if (月次計画期s.includes(0)) {
      this.add月次決算期s(決算期, true);
    }
    // 0期
    if (include計画決算期) {
      this.add年次決算期(決算期, Eフェーズ種別.計画フェーズ, 0, isWithPrefix);
    }
    // 1期の月次
    if (月次計画期s.includes(1) && c計画作成期間 > 0) {
      this.add月次決算期s(決算期, false, 1);
    }
    // 1期～10期
    if (include計画決算期) {
      for (let i = 1; i <= c計画作成期間; i++) {
        this.add年次決算期(決算期, Eフェーズ種別.計画フェーズ, i, isWithPrefix);
      }
    }
    // fixMonth設定
    if (isWith決算期Month) {
      決算期.fixMonth = this.getMonth(計画書.c計画0期の決算期);
    }

    return 決算期;
  }

  /**
   * 年次の決算期を追加します
   *
   * @param 決算期
   * @param eフェーズ種別
   * @param diffYear
   * @param isWithPrefix
   * @returns
   */
  private static add年次決算期(決算期: 決算期, eフェーズ種別: Eフェーズ種別, diffYear = 0, isWithPrefix = false): void {
    const 計画書 = StorageDao.get計画書();
    const c計画0期の決算年 = this.get計画0期の決算年();
    const c計画0期の決算月 = this.getMonth(計画書.c計画0期の決算期);

    const year = c計画0期の決算年 + diffYear;
    let label = '';
    if (isWithPrefix) {
      let prefix = '';
      switch (eフェーズ種別) {
        case Eフェーズ種別.分析フェーズ:
          prefix = diffYear === -1 ? '前期' : `${-diffYear}期前`;
          break;
        case Eフェーズ種別.計画フェーズ:
          prefix = `計画第${diffYear}期`;
          break;
        case Eフェーズ種別.モニタリングフェーズ:
          prefix = '予測値';
          break;
      }
      label += `${prefix}\n`;
    }
    label += AppFormatUtility.format年月期(`${year}${c計画0期の決算月}`);
    // header設定
    決算期.header.push({
      label,
      year,
      決算年: year,
    });
    // 決算年設定
    this.add決算年(決算期, year, eフェーズ種別);
  }

  /**
   * 月次の決算期を追加します
   *
   * @param 決算期
   * @param isWith実績見込Prefix
   * @param 期数
   * @returns
   */
  private static add月次決算期s(決算期: 決算期, isWith実績見込Prefix?: boolean, 期数 = 0): void {
    const 年月s = this.get計画期年月s(期数);
    const 実績直近月 = StorageDao.get計画書().c実績直近月;
    const 決算年 = this.get計画0期の決算年() + 期数;

    年月s.forEach((row) => {
      const ymNumber = row.year * 100 + row.month;
      let label = AppFormatUtility.format年月(ymNumber);
      let disabled = false;
      if (isWith実績見込Prefix !== undefined) {
        const prefix = isWith実績見込Prefix ? (ymNumber <= 実績直近月 ? '実績' : '見込') : `計画第${期数}期`;
        disabled = prefix === '実績';
        label = `${prefix}\n${label}`;
      }
      // header設定
      決算期.header.push({
        label,
        year: row.year,
        month: row.month,
        決算年,
        disabled,
      });
    });

    // 決算年設定
    this.add決算年(決算期, 年月s[年月s.length - 1].year, Eフェーズ種別.計画フェーズ);
  }

  /**
   * 決算年を追加します。
   *
   * @param 決算期
   * @param year
   * @param eフェーズ種別
   * @returns
   */
  private static add決算年(決算期: 決算期, year: number, eフェーズ種別: Eフェーズ種別): void {
    const found決算年 = 決算期.years.find((決算年) => 決算年.year === year);
    if (found決算年 === undefined) {
      決算期.years.push({
        year,
        eフェーズ種別,
        is計画01: this.is計画01(year),
      });
    }
  }
}
